export interface SmallGroupMember {
  id: string;
  userId?: number | null;
  name: string;
  dept?: string;
  manualEntry?: boolean;
}

export interface SmallGroup {
  groupId: number;
  groupNo?: string | null;
  groupName: string;
  leaderUserId?: number | null;
  leaderName?: string | null;
  secretaryUserId?: number | null;
  secretaryName?: string | null;
  memberEntries?: SmallGroupMember[] | null;
  memberUserIds?: number[] | null;
  members?: string | null;
  memberCount?: number | null;
  theme?: string | null;
  purpose?: string | null;
  activityGoal?: string | null;
  meetingCycle?: string | null;
  meetingPlace?: string | null;
  formedDate?: string | null;
  closedDate?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  remark?: string | null;
}

export interface SmallGroupPayload {
  groupNo?: string;
  groupName: string;
  leaderUserId?: number | null;
  secretaryUserId?: number | null;
  members?: SmallGroupMember[];
  memberUserIds?: number[];
  theme?: string;
  purpose?: string;
  activityGoal?: string;
  meetingCycle?: string;
  meetingPlace?: string;
  formedDate?: string;
  closedDate?: string;
  status?: string;
  remark?: string;
}

function newMemberId() {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createManualSmallGroupMember(
  name: string,
  dept?: string,
): SmallGroupMember {
  return {
    id: newMemberId(),
    userId: null,
    name: name.trim(),
    dept: (dept || '').trim(),
    manualEntry: true,
  };
}

export function createOrgSmallGroupMember(input: {
  userId: number;
  name: string;
  dept?: string;
}): SmallGroupMember {
  return {
    id: `u-${input.userId}`,
    userId: input.userId,
    name: input.name,
    dept: (input.dept || '').trim(),
    manualEntry: false,
  };
}

export function parseSmallGroupMembers(row: {
  memberEntries?: SmallGroupMember[] | null;
  memberUserIds?: number[] | null;
}): SmallGroupMember[] {
  if (Array.isArray(row.memberEntries) && row.memberEntries.length > 0) {
    return row.memberEntries.map((m) => ({
      id:
        m.id ||
        (m.userId != null
          ? `u-${m.userId}`
          : newMemberId()),
      userId: m.manualEntry ? null : m.userId ?? null,
      name: (m.name || '').trim(),
      dept: (m.dept || '').trim(),
      manualEntry: Boolean(m.manualEntry) || m.userId == null,
    }));
  }
  return (row.memberUserIds || []).map((userId) =>
    createOrgSmallGroupMember({ userId, name: `사용자 ${userId}` }),
  );
}

export function serializeSmallGroupMembers(
  members: SmallGroupMember[],
): SmallGroupMember[] {
  const out: SmallGroupMember[] = [];
  for (const m of members) {
    const manual = Boolean(m.manualEntry) || m.userId == null;
    const name = (m.name || '').trim();
    if (!name && m.userId == null) continue;
    out.push({
      id: m.id,
      userId: manual ? null : m.userId ?? null,
      name: name || (m.userId != null ? `사용자 ${m.userId}` : ''),
      dept: (m.dept || '').trim(),
      manualEntry: manual,
    });
  }
  return out;
}

export interface SmallGroupPlanItem {
  id: string;
  topic: string;
  schedule: string;
  method: string;
  owner: string;
}

export interface SmallGroupActivityPlan {
  planId: number;
  groupId: number;
  groupName?: string | null;
  planNo?: string | null;
  planYear?: number | null;
  title?: string | null;
  objective?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  itemsJson?: string | null;
  expectedEffect?: string | null;
  preparedBy?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  remark?: string | null;
}

export interface SmallGroupActivityPlanPayload {
  groupId: number;
  planNo?: string;
  planYear?: number;
  title?: string;
  objective?: string;
  periodFrom?: string;
  periodTo?: string;
  itemsJson?: string;
  expectedEffect?: string;
  preparedBy?: string;
  status?: string;
  remark?: string;
}

export interface SmallGroupActivity {
  activityId: number;
  groupId: number;
  groupName?: string | null;
  recordNo?: string | null;
  activityDate?: string | null;
  title?: string | null;
  location?: string | null;
  attendees?: string | null;
  content?: string | null;
  resultText?: string | null;
  nextPlan?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  remark?: string | null;
}

export interface SmallGroupActivityPayload {
  groupId: number;
  recordNo?: string;
  activityDate?: string;
  title: string;
  location?: string;
  attendees?: string;
  content?: string;
  resultText?: string;
  nextPlan?: string;
  status?: string;
  remark?: string;
}

export interface SmallGroupEvalCriterion {
  id: string;
  criterion: string;
  score: string;
  comment: string;
}

export interface SmallGroupActivityEvaluation {
  evaluationId: number;
  groupId: number;
  groupName?: string | null;
  evaluationNo?: string | null;
  evaluationDate?: string | null;
  title?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  criteriaJson?: string | null;
  scoreText?: string | null;
  strengths?: string | null;
  improvements?: string | null;
  overallComment?: string | null;
  evaluator?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  canRevoke?: boolean | null;
  remark?: string | null;
}

export interface SmallGroupActivityEvaluationPayload {
  groupId: number;
  evaluationNo?: string;
  evaluationDate?: string;
  title?: string;
  periodFrom?: string;
  periodTo?: string;
  criteriaJson?: string;
  scoreText?: string;
  strengths?: string;
  improvements?: string;
  overallComment?: string;
  evaluator?: string;
  status?: string;
  remark?: string;
}
